import { lazy, Suspense, type ReactNode } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DetailsSection, ErrorState, EntityHeader, PageLayout } from '@eis/patterns';
import { useGenerations, useTarif } from '../api/tarifs';

/** Lazily loaded: the authoring editors are their own chunk (see apps/eis-console/vite.config). */
const TarifAssets = lazy(async () => ({
  default: (await import('../../tarif-authoring/ui/TarifAssets')).TarifAssets,
}));
const TarifAddGenerationPanel = lazy(async () => ({
  default: (await import('../../tarif-authoring/ui/AddGenerationPanel')).TarifAddGenerationPanel,
}));
const GenerationPreview = lazy(async () => ({
  default: (await import('../../tarif-authoring/ui/deckung/overview/GenerationPreview'))
    .GenerationPreview,
}));

/**
 * One tarif and its generations.
 *
 * Two reads rather than one, because the service answers two different questions: `useTarif`
 * carries the ResourceVersion a write would quote back, and `useGenerations` carries none — a
 * list is not a resource an If-Match could guard (§B.10). Keeping them apart is what stops a
 * publish being guarded by a precondition read off a row in a list.
 */
export function TarifDetailPage({ slug }: { readonly slug: string }): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const tarif = useTarif(slug);
  const generations = useGenerations(slug);

  if (tarif.isError || generations.isError) {
    return (
      <PageLayout>
        <ErrorState
          description={t('tarifs.error.detail')}
          details={(tarif.error ?? generations.error)?.message}
          onRetry={() => {
            void tarif.refetch();
            void generations.refetch();
          }}
        />
      </PageLayout>
    );
  }

  if (tarif.data === undefined) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={220} />
      </PageLayout>
    );
  }

  const entity = tarif.data.tarif;
  const rows = generations.data?.generations ?? [];

  return (
    <PageLayout
      header={
        <EntityHeader
          title={entity?.name ?? slug}
          subtitle={entity?.description}
          // Berry has no breadcrumb band: the way back is the header's own link (prompt 24).
          backTo={{
            label: t('tarifs.title'),
            href: '/insurance/tarifs',
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: '/insurance/tarifs' });
            },
          }}
          meta={[
            { label: t('tarifs.detail.slug'), value: entity?.slug },
            { label: t('tarifs.detail.ern'), value: entity?.ern },
          ]}
        />
      }
    >
      <DetailsSection
        title={t('tarifs.detail.identity')}
        fields={[
          { label: t('tarifs.detail.name'), value: entity?.name },
          { label: t('tarifs.detail.generationCount'), value: rows.length },
        ]}
        advancedFields={[
          { label: t('tarifs.detail.ern'), value: entity?.ern },
          { label: t('tarifs.detail.createdBy'), value: entity?.createdBy },
        ]}
      />

      {/*
        The assets, before the generations. That is the order the work happens in: a generation
        cannot be published until the assets its manifest pins exist and are published (prompt
        14.3's guard), so an operator arriving on a new tarif needs the asset lists first.
      */}
      <Suspense fallback={<Skeleton variant="rounded" height={240} />}>
        <TarifAssets slug={slug} />
      </Suspense>

      {/*
        The tarif generation preview (ui-spec §10): every generation, its versions and the variants
        each version's pinned "Tarif & Deckung" offers. Its footer is the create-generation flow.
      */}
      <Suspense fallback={<Skeleton variant="rounded" height={160} />}>
        <GenerationPreview
          owner="tarif"
          slug={slug}
          ownerLabel={entity?.name ?? slug}
          generations={rows}
          isPending={generations.isPending}
          emptyText={t('tarifs.generations.empty')}
          addGeneration={
            <TarifAddGenerationPanel
              variant="footer"
              slug={slug}
              // The tarif's row version, read here and quoted back on the write (decision T-12):
              // adding a generation changes the tarif's generation set.
              ownerVersion={tarif.data.version?.version}
              onGoToAssets={() => {
                document.querySelector('[data-eis-assets]')?.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          }
        />
      </Suspense>
    </PageLayout>
  );
}
